import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, ShieldCheck } from 'lucide-react';
import { Logo } from './Logo';

const NAV = [
  { to: '/private-experiences', label: 'Private Experiences' },
  { to: '/signature-journeys', label: 'Signature Journeys' },
  { to: '/corporate', label: 'Corporate' },
  { to: '/gift', label: 'Gift' },
  { to: '/about', label: 'About' },
  { to: '/enquire', label: 'Enquire' },
];

function reviewerSignOut() {
  try { window.sessionStorage.removeItem('tdt_review_token'); } catch {}
  window.location.href = '/';
}

function isAdminSignedIn() {
  try { return !!window.sessionStorage.getItem('tdt_admin_token'); } catch { return false; }
}

// Header adapts: transparent + light logo on the home hero, solid ivory + dark logo elsewhere
export function Header() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [adminSignedIn, setAdminSignedIn] = useState(isAdminSignedIn());

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setAdminSignedIn(isAdminSignedIn()); // refresh badge state on route change
  }, [location.pathname]);

  const overHero = isHome && !scrolled;
  const textColor = overHero ? 'text-[color:var(--tdt-ivory)]' : 'text-[color:var(--tdt-ink)]';
  const bg = overHero
    ? 'bg-transparent'
    : 'bg-[color:var(--tdt-ivory)]/95 backdrop-blur-sm border-b border-[color:var(--tdt-border)]';

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${bg} ${textColor}`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 lg:py-5 flex items-center justify-between gap-3">
        <Link to="/" data-testid="logo-link" className="flex items-center min-w-0">
          <Logo size="md" tone={overHero ? 'light' : 'dark'} dropShadow={overHero} />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace('/', '')}`}
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            >
              {n.label}
            </NavLink>
          ))}
          {adminSignedIn && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) => `nav-link inline-flex items-center gap-1.5 text-[color:var(--tdt-gold)] ${isActive ? 'active' : ''}`}
            >
              <ShieldCheck size={14} /> Admin
            </NavLink>
          )}
          <button
            data-testid="reviewer-signout"
            onClick={reviewerSignOut}
            title="End review session"
            aria-label="End review session"
            className="nav-link opacity-60 hover:opacity-100 inline-flex items-center gap-1.5"
          >
            <LogOut size={14} />
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 -mr-1 shrink-0"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          style={overHero && !open ? { filter: 'drop-shadow(0 1px 6px rgba(0,0,0,0.7))' } : undefined}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[color:var(--tdt-ink)] text-[color:var(--tdt-ivory)] border-t border-[color:var(--tdt-border-light)]">
          <div className="px-6 py-8 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.to.replace('/', '')}`}
                className="nav-link"
              >
                {n.label}
              </NavLink>
            ))}
            {adminSignedIn && (
              <NavLink to="/admin" data-testid="mobile-nav-admin" className="nav-link inline-flex items-center gap-2 text-[color:var(--tdt-gold)]">
                <ShieldCheck size={14} /> Admin
              </NavLink>
            )}
            <button
              data-testid="mobile-reviewer-signout"
              onClick={reviewerSignOut}
              className="nav-link opacity-70 hover:opacity-100 inline-flex items-center gap-2 self-start"
            >
              <LogOut size={14} /> End review session
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[color:var(--tdt-ink)] text-[color:var(--tdt-ivory)] mt-0 relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-14 sm:py-20 lg:py-28 grid lg:grid-cols-12 gap-10 sm:gap-12">
        <div className="lg:col-span-5">
          <Logo size="md" tone="light" dropShadow={false} className="mb-6" />
          <p className="font-serif text-xl sm:text-2xl lg:text-3xl leading-snug max-w-md text-[color:var(--tdt-cream)]">
            Private culinary journeys, exquisitely curated.
          </p>
          <div className="hr-gold mt-6 sm:mt-8" />
        </div>

        <div className="lg:col-span-4">
          <div className="tdt-eyebrow tdt-eyebrow-light mb-4 sm:mb-5">Explore</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/private-experiences" className="hover:text-[color:var(--tdt-gold)] transition-colors">Private Experiences</Link></li>
            <li><Link to="/signature-journeys" className="hover:text-[color:var(--tdt-gold)] transition-colors">Signature Journeys</Link></li>
            <li><Link to="/corporate" className="hover:text-[color:var(--tdt-gold)] transition-colors">Corporate &amp; VIP Hosting</Link></li>
            <li><Link to="/gift" className="hover:text-[color:var(--tdt-gold)] transition-colors">Gift Experiences</Link></li>
            <li><Link to="/about" className="hover:text-[color:var(--tdt-gold)] transition-colors">About</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <div className="tdt-eyebrow tdt-eyebrow-light mb-5">Concierge</div>
          <p className="text-[15px] sm:text-sm text-[color:var(--tdt-cream)] leading-relaxed mb-6">
            By private appointment only. Each journey is shaped around the occasion, guests, and desired level of indulgence.
          </p>
          <Link to="/enquire" data-testid="footer-enquire-btn" className="btn-pill btn-outline-ivory text-xs">
            Enquire Privately
          </Link>
        </div>
      </div>

      <div className="border-t border-[color:var(--tdt-border-light)]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-5 sm:py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[10px] sm:text-xs tracking-widest uppercase text-[color:var(--tdt-cream)]/70">
          <div>
            &copy; {new Date().getFullYear()} Tour de Taste &middot; All experiences curated privately
            <a
              href="/admin"
              data-testid="footer-admin-link"
              aria-label="Admin"
              title="Admin"
              className="text-[color:var(--tdt-cream)]/35 hover:text-[color:var(--tdt-gold)] transition-colors ml-1"
            >
              &middot;
            </a>
          </div>
          <div className="flex items-center gap-4 sm:gap-6">
            <span>Discreet</span><span>Hosted</span><span>Tailored</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const location = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [location.pathname]);
  return (
    <div className="App min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 page-enter">{children}</main>
      <Footer />
    </div>
  );
}
